import { FC } from 'react';

import { Stack } from '@material-ui/core';
import { Box, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { CaretDoubleLeft, CaretDoubleRight } from 'phosphor-react';
import CoreModulesIcon from 'public/v2/icons/asterisk.svg';
import PercentageIcon from 'public/v2/icons/percentage.svg';
import ReceiptIcon from 'public/v2/icons/receipt.svg';
import StakeIcon from 'public/v2/icons/stake.svg';
import VaultIcon from 'public/v2/icons/vault.svg';
import WalletIcon from 'public/v2/icons/wallet.svg';

import { useAppSettings } from '~components/providers/SettingsProvider';
import { createFadeSlideAnimation } from '~components/theme/styles/animations';
import { secondaryColors, white } from '~components/theme/styles/colors';
import { ENativeAsset } from '~config/types';

import { V2_SIDEBAR_WIDTH_COLLAPSED_PX, V2_SIDEBAR_WIDTH_PX } from '../../_constants';
import { AssetIcon } from '../../ui/icons/assets';

import { SidebarItem } from './sidebarItem';

export const SideBarList: FC<{ width?: string; collapsed?: boolean; onClick?: () => void }> = ({
  width,
  onClick,
  collapsed = false,
}) => {
  const { asPath } = useRouter();

  const sideBarLinks = [
    { icon: <WalletIcon width="24px" height="24px" />, href: '/portfolio', text: 'PORTFOLIO' },
    { icon: <VaultIcon width="24px" height="24px" />, href: '/vaults', text: 'VAULTS' },
    { icon: <StakeIcon width="24px" height="24px" />, href: '/staking', text: 'STAKING' },
    { icon: <CoreModulesIcon width="24px" height="24px" />, href: '/coreModules', text: 'CORE MODULES' },
    { icon: <ReceiptIcon width="24px" height="24px" />, href: '/collectibles', text: 'COLLECTIBLES' },
    { icon: <PercentageIcon width="24px" height="24px" />, href: '/stats', text: 'STATISTICS' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {sideBarLinks.map(({ href, icon, text }) => {
        const active = asPath.search(`^${href}(.*)`) === 0;
        return (
          <SidebarItem
            key={href}
            width={width}
            collapsed={collapsed}
            active={active}
            href={href}
            icon={
              <Box
                component="span"
                sx={{
                  height: '24px',
                  svg: {
                    path: {
                      fill: active ? secondaryColors[200] : undefined,
                    },
                  },
                }}
              >
                {icon}
              </Box>
            }
            text={text}
            onClick={onClick}
          />
        );
      })}
    </Box>
  );
};

export const Sidebar: FC = () => {
  const { getSetting, saveSettings } = useAppSettings();

  const collapsed = getSetting('sidebarCollapsed');

  return (
    <Box
      sx={{
        position: 'fixed',
        width: collapsed ? V2_SIDEBAR_WIDTH_COLLAPSED_PX : V2_SIDEBAR_WIDTH_PX,
        borderRight: `1px solid ${white['15%']}`,
        height: '100%',
        overflow: 'hidden',
        transition: 'width 0.15s linear',
        animation: createFadeSlideAnimation(0),
      }}
    >
      <Box sx={{ padding: '20px 16px', borderBottom: `1px solid ${white['15%']}` }}>
        {collapsed ? (
          <Box mb={-0.25} ml={0.5} mt={0.25}>
            <AssetIcon noBg noBorder size={32} asset={ENativeAsset.VSTA} />
          </Box>
        ) : (
          <Typography
            variant="h6"
            sx={{
              color: white['15%'],
              display: 'flex',
              gap: '1em',
              justifyContent: 'space-around',
              textAlign: 'center',
            }}
          >
            <span>V</span>
            <span>E</span>
            <span>S</span>
            <span>T</span>
            <span>A</span>
          </Typography>
        )}
      </Box>
      <Stack height={'calc(100% - 86px)'} justifyContent={'space-between'}>
        <SideBarList collapsed={collapsed} />
        <Box
          pl={2.5}
          onClick={() => saveSettings({ sidebarCollapsed: !collapsed }, false)}
          sx={{ cursor: 'pointer' }}
        >
          {collapsed ? (
            <CaretDoubleRight size={26} color="#b3b4c1" />
          ) : (
            <CaretDoubleLeft size={26} color="#b3b4c1" />
          )}
        </Box>
      </Stack>
    </Box>
  );
};
